import React from 'react';
import { TrendingUp, TrendingDown, AlertCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../i18n/useTranslation';
import type { RiskAnalysis } from '../services/riskAnalysisService';

interface RiskAnalysisSectionProps {
  riskAnalysis: RiskAnalysis | null;
}

const RiskAnalysisSection: React.FC<RiskAnalysisSectionProps> = ({ riskAnalysis }) => {
  const { t } = useTranslation();

  if (!riskAnalysis) {
    return (
      <div className="bg-white rounded-lg shadow mb-8">
        <div className="p-6">
          <h2 className="text-xl font-bold mb-2">{t('analytics.riskAnalysis')}</h2>
          <p className="text-muted text-sm mb-6">{t('analytics.riskAnalysisCaption')}</p>
          <div className="bg-gray-50 rounded-lg p-8 text-center">
            <p className="text-gray-600">
              {t('analytics.addMoreGoalsToSeeAnalysis')}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const cards = [
    {
      title: t('analytics.topStrength'),
      icon: TrendingUp,
      value: riskAnalysis.topStrength.area,
      percentage: `${riskAnalysis.topStrength.savePercentage}%`,
      breakdown: `(${riskAnalysis.topStrength.goals} of ${riskAnalysis.topStrength.totalShots} goals)`,
      bgColor: 'bg-slate-50',
      iconColor: 'text-primary-600',
      textColor: 'text-slate-700',
      percentageColor: 'text-primary-700'
    },
    {
      title: t('analytics.topWeakness'), 
      icon: TrendingDown,
      value: riskAnalysis.topWeakness.area,
      percentage: `${riskAnalysis.topWeakness.goalPercentage}%`,
      breakdown: `(${riskAnalysis.topWeakness.goals}/${riskAnalysis.topWeakness.totalShots} goals)`,
      bgColor: 'bg-slate-50',
      iconColor: 'text-slate-600',
      textColor: 'text-slate-700',
      percentageColor: 'text-slate-700'
    },
    {
      title: t('analytics.mostRiskySituation'),
      icon: AlertCircle,
      value: riskAnalysis.mostRiskySituation.situation,
      percentage: `${riskAnalysis.mostRiskySituation.goalPercentage}%`,
      breakdown: `(${riskAnalysis.mostRiskySituation.goals} of ${riskAnalysis.mostRiskySituation.totalShots} goals)`,
      bgColor: 'bg-slate-50',
      iconColor: 'text-amber-600',
      textColor: 'text-slate-700',
      percentageColor: 'text-amber-700'
    },
    {
      title: t('analytics.mostRiskyShotType'),
      icon: AlertTriangle,
      value: riskAnalysis.mostRiskyShotType.shotType,
      percentage: `${riskAnalysis.mostRiskyShotType.goalPercentage}%`,
      breakdown: `(${riskAnalysis.mostRiskyShotType.goals} of ${riskAnalysis.mostRiskyShotType.totalShots} goals)`,
      bgColor: 'bg-slate-50',
      iconColor: 'text-red-600',
      textColor: 'text-slate-700',
      percentageColor: 'text-red-700'
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow mb-8">
      <div className="p-6">
        <h2 className="text-xl font-bold mb-2">{t('analytics.riskAnalysis')}</h2>
        <p className="text-muted text-sm mb-6">{t('analytics.riskAnalysisCaption')}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {cards.map((card, index) => {
            const Icon = card.icon;
            return (
              <div key={index} className={`${card.bgColor} rounded-lg p-4 border border-slate-200`}>
                <div className="flex items-center justify-between mb-3">
                  <Icon className={`h-6 w-6 ${card.iconColor}`} />
                  <div className="text-right">
                    <div className={`text-lg font-bold ${card.percentageColor}`}>
                      {card.percentage}
                    </div>
                    <div className="text-xs text-slate-500">
                      {card.breakdown}
                    </div>
                  </div>
                </div>
                
                <h3 className="font-semibold text-slate-900 text-sm mb-1">
                  {card.title}
                </h3>
                
                <div className={`font-medium ${card.textColor}`}>
                  {card.value}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default RiskAnalysisSection;